import { useEffect, useState, useRef } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { useAppSelector } from 'app';
import DepartmentAPI from 'components/setting/api/department-api';
import { FB_OAUTH_STATE_KEY, FB_OAUTH_RETURN_PATH_KEY, FB_OAUTH_LOCATION_ID_PARAM, FB_OAUTH_STATUS_PARAM, OAuthCallbackStatus, FBOAuthRedirectStatus } from './facebook-oauth-constants';

interface OAuthState {
  dealerId: number;
  locationId: number;
  crmUserId: string;
}

const FacebookOAuthCallback: React.FC = () => {
  const [status, setStatus] = useState<OAuthCallbackStatus>(OAuthCallbackStatus.PROCESSING);
  const [errorMessage, setErrorMessage] = useState('');
  const processedRef = useRef(false);
  const authToken = useAppSelector((state) => state.user.authToken);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const handleOAuthCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const error = params.get('error');
      const returnPath = sessionStorage.getItem(FB_OAUTH_RETURN_PATH_KEY) || '/settings';

      // Read state early so locationId is available for all redirect cases
      const stateJson = sessionStorage.getItem(FB_OAUTH_STATE_KEY);
      let oauthState: OAuthState | null = null;
      if (stateJson) {
        try {
          oauthState = JSON.parse(stateJson);
        } catch {
          // Invalid state, will be handled below
        }
      }

      // Helper to build redirect URL with locationId and fb_status when available
      const buildRedirectUrl = (path: string, fbStatus?: string) => {
        const [pathPart, hashPart] = path.split('#');
        const params = new URLSearchParams();
        if (oauthState?.locationId) params.set(FB_OAUTH_LOCATION_ID_PARAM, String(oauthState.locationId));
        if (fbStatus) params.set(FB_OAUTH_STATUS_PARAM, fbStatus);
        const queryString = params.toString() ? `?${params.toString()}` : '';
        return `${window.location.origin}${pathPart}${queryString}${hashPart ? '#' + hashPart : ''}`;
      };

      // User denied or cancelled
      if (error) {
        sessionStorage.removeItem(FB_OAUTH_STATE_KEY);
        sessionStorage.removeItem(FB_OAUTH_RETURN_PATH_KEY);
        window.location.replace(buildRedirectUrl(returnPath, FBOAuthRedirectStatus.CANCELLED));
        return;
      }

      if (!code) {
        setStatus(OAuthCallbackStatus.ERROR);
        setErrorMessage('No authorization code received from Facebook.');
        setTimeout(() => { window.location.replace(buildRedirectUrl(returnPath)); }, 3000);
        return;
      }

      if (!oauthState) {
        setStatus(OAuthCallbackStatus.ERROR);
        setErrorMessage('Session expired. Please try linking Facebook again.');
        setTimeout(() => { window.location.replace(buildRedirectUrl(returnPath)); }, 3000);
        return;
      }

      try {
        // Wait for auth token to be available (needed for API call)
        if (!authToken) {
          setStatus(OAuthCallbackStatus.ERROR);
          setErrorMessage('Authentication session not found. Please log in and try again.');
          setTimeout(() => { window.location.replace(buildRedirectUrl(returnPath)); }, 3000);
          return;
        }

        const redirectUri = `${window.location.origin}/facebook-callback`;

        await DepartmentAPI.exchangeFacebookOAuthCode({
          dealerId: oauthState.dealerId,
          locationId: oauthState.locationId,
          code,
          redirectUri,
          crmUserId: oauthState.crmUserId,
        });

        setStatus(OAuthCallbackStatus.SUCCESS);

        // Cleanup and redirect
        sessionStorage.removeItem(FB_OAUTH_STATE_KEY);
        sessionStorage.removeItem(FB_OAUTH_RETURN_PATH_KEY);

        const redirectUrl = buildRedirectUrl(returnPath, FBOAuthRedirectStatus.SUCCESS);
        
        setTimeout(() => {
          window.location.replace(redirectUrl);
        }, 1500);

      } catch (error) {
        console.error('[FB OAuth Callback] Error:', error);
        setStatus(OAuthCallbackStatus.ERROR);
        const msg = typeof error === 'object' && error !== null && 'message' in error
          ? (error as { message: string }).message
          : 'Failed to link Facebook account. Please try again.';
        setErrorMessage(msg);
        setTimeout(() => {
          window.location.replace(buildRedirectUrl(returnPath, FBOAuthRedirectStatus.ERROR));
        }, 3000);
      }
    };

    handleOAuthCallback();
  }, [authToken]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="60vh"
      style={{ gap: '16px' }}
    >
      {status === OAuthCallbackStatus.PROCESSING && (
        <>
          <CircularProgress size={40} />
          <Typography variant="h6">Linking your Facebook account...</Typography>
          <Typography variant="body2" color="textSecondary">
            Please wait while we complete the connection.
          </Typography>
        </>
      )}
      {status === OAuthCallbackStatus.SUCCESS && (
        <>
          <Typography variant="h6" style={{ color: '#4caf50' }}>
            Facebook account linked successfully!
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Redirecting back to settings...
          </Typography>
        </>
      )}
      {status === OAuthCallbackStatus.ERROR && (
        <>
          <Typography variant="h6" color="error">
            {errorMessage}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Redirecting back to settings...
          </Typography>
        </>
      )}
    </Box>
  );
};

export default FacebookOAuthCallback;
